import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGame } from "../../context/GameContext";
import { useSocket } from "../../context/SocketContext";

const RoomPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const { gameState, readyUp, startGame } = useGame();
  const { isConnected } = useSocket();
  const navigate = useNavigate();

  // Redirect if no roomId or if game started
  useEffect(() => {
    if (!roomId || !gameState.roomId) {
      navigate("/");
      return;
    }

    if (gameState.isGameStarted) {
      navigate(`/game/${roomId}`);
    }
  }, [roomId, gameState.roomId, gameState.isGameStarted, navigate]);

  const handleReadyUp = () => {
    readyUp();
  };

  const handleStartGame = () => {
    startGame();
  };

  const isAdmin = gameState.adminId === gameState.myId;
  const isPlayerReady = gameState.players.find(
    (p) => p.id === gameState.myId
  )?.isReady;
  const allPlayersReady =
    gameState.players.length > 0 &&
    gameState.players.every((player) => player.isReady);
  const canStartGame =
    isAdmin && allPlayersReady && gameState.players.length >= 4;

  if (!isConnected || !gameState.roomId) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 p-4">
        <div className="card-transparent">
          <p className="text-center">Connecting to game server...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 p-4">
      <div className="w-full max-w-2xl">
        <div className="card-transparent mb-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold">Waiting Room</h1>
            <div className="bg-slate-700 rounded-md px-3 py-1 text-sm">
              Room Code: <span className="font-bold">{gameState.roomCode}</span>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              Players ({gameState.players.length}/10)
            </h2>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {gameState.players.map((player) => (
                <div
                  key={player.id}
                  className={`p-3 rounded-md ${
                    player.isReady ? "bg-green-800" : "bg-slate-700"
                  } flex items-center`}
                >
                  <span className="flex-grow">{player.name}</span>
                  {player.id === gameState.adminId && (
                    <span className="text-xs bg-indigo-600 px-2 py-1 rounded">
                      Admin
                    </span>
                  )}
                  {player.isReady && (
                    <span className="ml-2 text-green-500">✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {!isPlayerReady ? (
              <button
                onClick={handleReadyUp}
                className="btn btn-primary flex-grow"
              >
                Ready Up
              </button>
            ) : (
              <button
                disabled
                className="btn btn-secondary flex-grow opacity-50"
              >
                Ready ✓
              </button>
            )}

            {isAdmin ? (
              <button
                onClick={handleStartGame}
                disabled={!canStartGame}
                className={`btn ${
                  canStartGame ? "btn-primary" : "btn-secondary opacity-50"
                } flex-grow`}
              >
                Start Game
              </button>
            ) : (
              <div className="text-center text-sm text-slate-400 p-2">
                Waiting for admin to start the game...
              </div>
            )}
          </div>

          {gameState.players.length < 4 && (
            <div className="mt-4 text-amber-500 text-sm text-center">
              At least 4 players are needed to start the game.
            </div>
          )}
        </div>

        <div className="card-transparent">
          <h2 className="text-lg font-semibold mb-4">How to Play</h2>
          <div className="text-sm text-slate-300 space-y-2">
            <p>
              1. Each player will be assigned a secret role: Villager, Mafia,
              Doctor, or Detective.
            </p>
            <p>
              2. During the night phase, Mafia will choose someone to kill,
              Doctor can save someone, and Detective can investigate someone.
            </p>
            <p>
              3. During the day phase, all players discuss and try to identify
              the Mafia.
            </p>
            <p>
              4. The game continues until either all Mafia are eliminated
              (villagers win) or Mafia outnumber the villagers (mafia wins).
            </p>
          </div>
        </div>

        <div className="mt-4 text-center">
          <button
            onClick={() => navigate("/")}
            className="text-sm text-slate-500 hover:text-slate-300"
          >
            Leave Room
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomPage;
